import React, { useState } from 'react';
import {
  Clock,
  MapPin,
  CheckCircle2,
  XCircle,
  AlertCircle,
  X,
} from 'lucide-react';

const AttendanceCalendar = ({ monthlyRecords, selectedMonth, selectedYear }) => {
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Utility functions
  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };

  const formatWorkDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      present: {
        bg: 'bg-green-50',
        border: 'border-green-300',
        text: 'text-green-700',
        dot: 'bg-green-500',
        label: 'Present',
        cellBg: 'bg-green-50 hover:bg-green-100',
      },
      absent: {
        bg: 'bg-red-50',
        border: 'border-red-300',
        text: 'text-red-700',
        dot: 'bg-red-500',
        label: 'Absent',
        cellBg: 'bg-red-50 hover:bg-red-100',
      },
      half_day: {
        bg: 'bg-amber-50',
        border: 'border-amber-300',
        text: 'text-amber-700',
        dot: 'bg-amber-500',
        label: 'Half Day',
        cellBg: 'bg-amber-50 hover:bg-amber-100',
      },
      leave: {
        bg: 'bg-blue-50',
        border: 'border-blue-300',
        text: 'text-blue-700',
        dot: 'bg-blue-500',
        label: 'Leave',
        cellBg: 'bg-blue-50 hover:bg-blue-100',
      },
      holiday: {
        bg: 'bg-purple-50',
        border: 'border-purple-300',
        text: 'text-purple-700',
        dot: 'bg-purple-500',
        label: 'Holiday',
        cellBg: 'bg-purple-50 hover:bg-purple-100',
      },
      weekend: {
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-400',
        dot: 'bg-gray-300',
        label: 'Weekend',
        cellBg: 'bg-gray-50',
      },
    };
    return (
      configs[status] || {
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-500',
        dot: 'bg-gray-300',
        label: status || '-',
        cellBg: 'bg-white',
      }
    );
  };

  // Build record map by day of month
  const recordMap = {};
  monthlyRecords.forEach((record) => {
    const date = new Date(record.date);
    const day = date.getDate();
    recordMap[day] = record;
  });

  // Calendar calculations
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const firstDayOfWeek = new Date(selectedYear, selectedMonth - 1, 1).getDay(); // 0 = Sunday

  // Build weeks array
  const weeks = [];
  let currentWeek = new Array(firstDayOfWeek).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  const today = new Date();
  const isToday = (day) =>
    day &&
    today.getDate() === day &&
    today.getMonth() + 1 === selectedMonth &&
    today.getFullYear() === selectedYear;

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div>
      {/* Calendar Grid */}
      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {/* Day name headers */}
        <div className="grid grid-cols-7 bg-gray-800">
          {dayNames.map((name, i) => (
            <div
              key={name}
              className={`py-2.5 text-center text-xs font-bold tracking-wide ${
                i === 0 ? 'text-red-300' : 'text-gray-200'
              }`}
            >
              {name}
            </div>
          ))}
        </div>

        {/* Calendar weeks */}
        {weeks.map((week, weekIdx) => (
          <div
            key={weekIdx}
            className="grid grid-cols-7 border-b border-gray-100 last:border-b-0"
          >
            {week.map((day, dayIdx) => {
              if (!day) {
                return (
                  <div
                    key={dayIdx}
                    className="min-h-[100px] bg-gray-50/60 border-r border-gray-100 last:border-r-0"
                  />
                );
              }

              const record = recordMap[day];
              const config = record
                ? getStatusConfig(record.status)
                : dayIdx === 0
                ? getStatusConfig('weekend')
                : {
                    bg: 'bg-white',
                    border: 'border-transparent',
                    text: 'text-gray-400',
                    dot: '',
                    label: '',
                    cellBg: 'bg-white',
                  };
              const isTodayDay = isToday(day);
              const hasRecord = !!record;

              return (
                <div
                  key={dayIdx}
                  onClick={() => hasRecord && setSelectedRecord(record)}
                  className={`min-h-[100px] p-1.5 border-r border-gray-100 last:border-r-0 transition-all relative
                    ${hasRecord ? 'cursor-pointer hover:shadow-lg hover:z-10' : ''}
                    ${isTodayDay ? 'ring-2 ring-blue-500 ring-inset z-10' : ''}
                    ${hasRecord ? config.cellBg : 'bg-white'}
                  `}
                >
                  {/* Day number + status dot */}
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-[11px] font-bold leading-none ${
                        isTodayDay
                          ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm'
                          : dayIdx === 0
                          ? 'text-red-500'
                          : 'text-gray-700'
                      }`}
                    >
                      {day}
                    </span>
                    {hasRecord && (
                      <span
                        className={`w-2.5 h-2.5 rounded-full ${config.dot} shadow-sm`}
                        title={config.label}
                      />
                    )}
                  </div>

                  {/* Status + details */}
                  {hasRecord && (
                    <div className="space-y-0.5">
                      <p
                        className={`text-[10px] font-bold capitalize ${config.text} leading-tight truncate`}
                      >
                        {record.status?.replace('_', ' ')}
                        {record.isLateArrival && ' 🕐'}
                      </p>

                      {/* Punch In time */}
                      {record.punchIn?.time && (
                        <div className="flex items-center gap-0.5">
                          <span className="text-[8px] text-green-600">▼</span>
                          <span className="text-[9px] text-gray-600 font-medium leading-tight">
                            {formatTime(record.punchIn.time)}
                          </span>
                        </div>
                      )}

                      {/* Punch Out time */}
                      {record.punchOut?.time && (
                        <div className="flex items-center gap-0.5">
                          <span className="text-[8px] text-red-500">▲</span>
                          <span className="text-[9px] text-gray-600 font-medium leading-tight">
                            {formatTime(record.punchOut.time)}
                          </span>
                        </div>
                      )}

                      {/* Work hours */}
                      {record.workDuration > 0 && (
                        <p className="text-[9px] font-semibold text-gray-700 bg-white/60 rounded px-0.5 leading-tight">
                          ⏱ {formatWorkDuration(record.workDuration)}
                        </p>
                      )}

                      {/* Indicators row */}
                      <div className="flex items-center gap-0.5 flex-wrap">
                        {record.violations?.length > 0 && (
                          <span className="text-[8px] bg-red-200 text-red-700 rounded px-1 font-bold">
                            ⚠{record.violations.length}
                          </span>
                        )}
                        {record.failedAttempts?.length > 0 && (
                          <span className="text-[8px] bg-rose-200 text-rose-700 rounded px-1 font-bold">
                            ✕{record.failedAttempts.length}
                          </span>
                        )}
                        {record.locationLogs?.some((l) => l.status === 'missed') && (
                          <span className="text-[8px] bg-orange-200 text-orange-700 rounded px-1 font-bold">
                            📍
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 px-1">
        {[
          { label: 'Present', color: 'bg-green-500' },
          { label: 'Absent', color: 'bg-red-500' },
          { label: 'Half Day', color: 'bg-amber-500' },
          { label: 'Leave', color: 'bg-blue-500' },
          { label: 'Holiday', color: 'bg-purple-500' },
          { label: 'Today', color: 'ring-2 ring-blue-500 bg-white' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm ${item.color}`} />
            <span className="text-xs text-gray-500">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedRecord && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedRecord(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {new Date(selectedRecord.date).toLocaleDateString('en-IN', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    timeZone: 'Asia/Kolkata',
                  })}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                      getStatusConfig(selectedRecord.status).bg
                    } ${getStatusConfig(selectedRecord.status).text} border ${
                      getStatusConfig(selectedRecord.status).border
                    }`}
                  >
                    {selectedRecord.status?.replace('_', ' ')}
                  </span>
                  {selectedRecord.isLateArrival && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300">
                      🕐 Late
                    </span>
                  )}
                  {selectedRecord.isEarlyDeparture && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-300">
                      ⏰ Early Departure
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedRecord(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Punch In / Punch Out / Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Punch In Card */}
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-1.5">
                    <Clock className="w-4 h-4" /> Punch In
                  </h4>
                  {selectedRecord.punchIn?.time ? (
                    <>
                      <p className="text-xl font-bold text-green-700">
                        {formatTime(selectedRecord.punchIn.time)}
                      </p>
                      {selectedRecord.punchIn.distanceFromOffice != null && (
                        <p className="text-xs text-green-600 mt-1">
                          📍{' '}
                          {selectedRecord.punchIn.distanceFromOffice >= 1000
                            ? `${(selectedRecord.punchIn.distanceFromOffice / 1000).toFixed(2)} km`
                            : `${Math.round(selectedRecord.punchIn.distanceFromOffice)}m`}
                        </p>
                      )}
                      {selectedRecord.punchIn.photo?.url && (
                        <a
                          href={selectedRecord.punchIn.photo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-2"
                        >
                          <img
                            src={selectedRecord.punchIn.photo.url}
                            alt="Punch In"
                            className="w-full h-28 object-cover rounded-lg border border-green-200"
                          />
                        </a>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">No punch in</p>
                  )}
                </div>

                {/* Punch Out Card */}
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1.5">
                    <Clock className="w-4 h-4" /> Punch Out
                  </h4>
                  {selectedRecord.punchOut?.time ? (
                    <>
                      <p className="text-xl font-bold text-red-700">
                        {formatTime(selectedRecord.punchOut.time)}
                      </p>
                      {selectedRecord.punchOut.distanceFromOffice != null && (
                        <p className="text-xs text-red-600 mt-1">
                          📍{' '}
                          {selectedRecord.punchOut.distanceFromOffice >= 1000
                            ? `${(selectedRecord.punchOut.distanceFromOffice / 1000).toFixed(2)} km`
                            : `${Math.round(selectedRecord.punchOut.distanceFromOffice)}m`}
                        </p>
                      )}
                      {selectedRecord.punchOut.photo?.url && (
                        <a
                          href={selectedRecord.punchOut.photo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-2"
                        >
                          <img
                            src={selectedRecord.punchOut.photo.url}
                            alt="Punch Out"
                            className="w-full h-28 object-cover rounded-lg border border-red-200"
                          />
                        </a>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">No punch out</p>
                  )}
                </div>

                {/* Details Card */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">📋 Details</h4>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Work Duration</span>
                      <span className="font-bold text-gray-800">
                        {formatWorkDuration(selectedRecord.workDuration)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Location Checks</span>
                      <span className="font-bold text-gray-800">
                        {selectedRecord.locationLogs?.length || 0}
                      </span>
                    </div>
                    {selectedRecord.isLateArrival && (
                      <div className="flex items-center gap-1.5 text-amber-600 bg-amber-50 rounded-lg px-2 py-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium">Late Arrival</span>
                      </div>
                    )}
                    {selectedRecord.isEarlyDeparture && (
                      <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50 rounded-lg px-2 py-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium">Early Departure</span>
                      </div>
                    )}
                    {selectedRecord.notes && (
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-400 mb-0.5">Notes</p>
                        <p className="text-sm text-gray-700">{selectedRecord.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Location Check Logs */}
              {selectedRecord.locationLogs?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-gray-600" /> Location Check Logs (
                    {selectedRecord.locationLogs.length})
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {[...selectedRecord.locationLogs]
                      .sort((a, b) => {
                        if (a.status === 'missed' && b.status !== 'missed') return -1;
                        if (a.status !== 'missed' && b.status === 'missed') return 1;
                        return (
                          new Date(a.timestamp || a.expectedTime) -
                          new Date(b.timestamp || b.expectedTime)
                        );
                      })
                      .map((log, idx) => {
                        const isMissed = log.status === 'missed';
                        const isOutOfRange = log.distanceFromOffice > 100;
                        return (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-2.5 rounded-lg text-sm ${
                              isMissed
                                ? 'bg-red-50 border border-red-200'
                                : isOutOfRange
                                ? 'bg-amber-50 border border-amber-200'
                                : 'bg-green-50 border border-green-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {isMissed ? (
                                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              ) : isOutOfRange ? (
                                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              ) : (
                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                              )}
                              <div>
                                <span className="font-medium">
                                  {formatTime(log.timestamp || log.expectedTime)}
                                </span>
                                <span className="text-xs text-gray-500 ml-1">
                                  ({log.type || 'periodic'})
                                </span>
                                {isMissed && log.expectedTime && (
                                  <span className="text-xs text-red-500 ml-1">
                                    Expected: {formatTime(log.expectedTime)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!isMissed && log.distanceFromOffice != null && (
                                <span
                                  className={`text-xs font-medium ${
                                    isOutOfRange ? 'text-amber-600' : 'text-green-600'
                                  }`}
                                >
                                  {log.distanceFromOffice >= 1000
                                    ? `${(log.distanceFromOffice / 1000).toFixed(2)} km`
                                    : `${Math.round(log.distanceFromOffice)}m`}
                                </span>
                              )}
                              {log.location?.latitude && (
                                <a
                                  href={`https://www.google.com/maps?q=${log.location.latitude},${log.location.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-700"
                                >
                                  📍
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Violations */}
              {selectedRecord.violations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-800 mb-2">
                    ⚠️ Violations ({selectedRecord.violations.length})
                  </h4>
                  <div className="space-y-1.5">
                    {selectedRecord.violations.map((v, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 p-2.5 bg-red-50 rounded-lg border border-red-200 text-sm"
                      >
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-red-700 capitalize">
                            {v.type?.replace(/_/g, ' ')}
                          </span>
                          <span className="text-red-600 ml-1">- {v.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Failed Punch Attempts */}
              {selectedRecord.failedAttempts?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-800 mb-2">
                    ❌ Failed Punch Attempts ({selectedRecord.failedAttempts.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedRecord.failedAttempts.map((attempt, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-red-50 rounded-lg border border-red-200"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-200 text-red-800 capitalize">
                            {attempt.attemptType}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatTime(attempt.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-red-700 font-medium mb-1">
                          ❌ {attempt.reason}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          <span>
                            Distance:{' '}
                            <strong className="text-red-600">
                              {attempt.distanceFromOffice >= 1000
                                ? `${(attempt.distanceFromOffice / 1000).toFixed(2)} km`
                                : `${Math.round(attempt.distanceFromOffice)}m`}
                            </strong>
                          </span>
                          {attempt.location?.latitude && (
                            <a
                              href={`https://www.google.com/maps?q=${attempt.location.latitude},${attempt.location.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              📍 View Location
                            </a>
                          )}
                        </div>
                        {attempt.photo?.url && (
                          <a
                            href={attempt.photo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block mt-2"
                          >
                            <img
                              src={attempt.photo.url}
                              alt="Failed attempt"
                              className="w-16 h-16 object-cover rounded border border-red-200"
                            />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceCalendar;
